/* eslint-disable no-restricted-syntax */
import {Locale} from '@aboutyou/config';
import {
  getDeprecatedStaticRoutesByLocale_UNSAFE,
  getStaticRoutesByLocale_UNSAFE,
} from './localizedUrls/getStaticRoutesByLocale';
import {
  LEGAL_PAGE_COMPONENT,
  getPageComponentKeysByLocale_UNSAFE,
  getStaticPagesByLocale_UNSAFE,
} from './localizedUrls/legal-pages';
import {LEGAL_PAGE_TYPE} from './localizedUrls/legalPagesRouteKeys';
import {
  getGenericUrls_UNSAFE,
  resolveGenericUrl_UNSAFE,
} from './localizedUrls/resolveGenericUrl';
import {RouteKey} from './localizedUrls/routeKeys';
import {
  helpCenterContactPageRoute,
  helpCenterLandingPageRoute,
} from './localizedUrls/routes.constants';
import {getSpecialSlugsByLocale_UNSAFE} from './localizedUrls/specialSlugs';
import {getTopCategorySlugsByLocale_UNSAFE} from './localizedUrls/topCategorySlugs';

const REG_EXP_LEADING_SLASH = /^\/+/;

export type RedirectsMap = {
  [key: string]: string | null;
};

export function getRedirectsMap_UNSAFE({
  locale,
  alternateLocales,
}: {
  locale: Locale;
  alternateLocales: Array<Locale>;
}): RedirectsMap {
  let redirects: RedirectsMap = {};
  getGenericUrls_UNSAFE().forEach(targetUrl => {
    redirects[targetUrl] = resolveGenericUrl_UNSAFE(targetUrl, Locale[locale]);
  });

  const {female, male} = getTopCategorySlugsByLocale_UNSAFE(locale);
  redirects = {
    ...redirects,
    '/sc/entdecke-angesagte-stories-153': `/${female}/stories`,
    '/sc/entdecke-angesagte-stories-154': `/${male}/stories`,
  };

  if (locale === 'de_DE') {
    redirects = {
      ...redirects,
      '/beachshop': `/s/beachshop-2623`,
    };
  }

  const legalPageComponents = getPageComponentKeysByLocale_UNSAFE(locale);
  let aboutUsRedirect = 'https://corporate.aboutyou.de/en/';
  if (locale === 'de_DE') {
    aboutUsRedirect = 'https://corporate.aboutyou.de/de/';
  }
  redirects = {
    ...redirects,
    [`/${legalPageComponents[LEGAL_PAGE_COMPONENT.AboutUs]}`]: aboutUsRedirect,
  };

  const faqUrl = getStaticPagesByLocale_UNSAFE(locale)[LEGAL_PAGE_TYPE.FAQ];
  redirects = {
    ...redirects,
    '/faq': helpCenterLandingPageRoute,
    '/faq/payment': helpCenterLandingPageRoute,
    [`/${faqUrl}`]: helpCenterLandingPageRoute,
  };

  const specialUrls = getSpecialSlugsByLocale_UNSAFE(locale);
  const staticUrls = getStaticRoutesByLocale_UNSAFE(locale);
  const contactUrl = specialUrls.contact;
  const deprecatedStaticRoutes =
    getDeprecatedStaticRoutesByLocale_UNSAFE(locale);

  // TLDR; check if we not trying to redirect the user to the same page
  //
  // Some countries used to have `/checkout` as default route
  // this prevents from having a route the redirects the user
  // from `/checkout` -> `/checkout` (same pathname)
  //
  // This was the case for the following countries:
  //    Locale.bg_BG
  //    Locale.da_DK
  //    Locale.en_ZZ
  //    Locale.en_IE
  //    Locale.en_DE
  //    Locale.ar_SA
  //    Locale.en_SA
  //    Locale.he_IL
  //    Locale.en_IL
  //    Locale.fi_FI
  if (deprecatedStaticRoutes[RouteKey.Checkout] !== 'checkout') {
    redirects = {
      ...redirects,
      // Important for Tracking - in case user lands on eg `/kasse`
      // needs to be redirected to /checkout
      [`/${deprecatedStaticRoutes[RouteKey.Checkout]}`]: '/checkout',
    };
  }

  if (deprecatedStaticRoutes[RouteKey.Search] !== 'search') {
    redirects = {
      ...redirects,
      // Important for Tracking - in case user lands on eg `/suche`
      // needs to be redirected to /search
      [`/${deprecatedStaticRoutes[RouteKey.Search]}`]: '/search',
    };
  }

  redirects = {
    ...redirects,
    [`/${faqUrl}/${contactUrl}`]: helpCenterContactPageRoute(contactUrl),
    [`/${contactUrl}`]: helpCenterContactPageRoute(contactUrl),
  };

  alternateLocales.forEach(altLocale => {
    const staticAlternateUrls = getStaticRoutesByLocale_UNSAFE(altLocale);
    redirects = {
      ...redirects,
      [`/${staticAlternateUrls[RouteKey.YourShop]}`]: `/${
        staticUrls[RouteKey.YourShop]
      }`,
      [`/${staticAlternateUrls[RouteKey.Basket]}`]: `/${
        staticUrls[RouteKey.Basket]
      }`,
      [`/${staticAlternateUrls[RouteKey.Wishlist]}`]: `/${
        staticUrls[RouteKey.Wishlist]
      }`,
      [`/${staticAlternateUrls[RouteKey.Brands]}`]: `/${
        staticUrls[RouteKey.Brands]
      }`,
      [`/${staticAlternateUrls[RouteKey.Imprint]}`]: `/${
        staticUrls[RouteKey.Imprint]
      }`,
    };
  });

  // filter urls that point to itself
  return Object.fromEntries(
    Object.entries(redirects)
      .filter(
        ([targetUrl, redirectUrl]) =>
          redirectUrl &&
          targetUrl.replace(REG_EXP_LEADING_SLASH, '') !==
            redirectUrl.replace(REG_EXP_LEADING_SLASH, ''),
      )
      // filter empty redirects from the base url, they should never redirect anywhere
      .filter(([targetUrl]) => targetUrl !== '/'),
  );
}
